import type { Plugin } from "vue";

import { localize, setLocale as setVeeValidateLocale } from "@vee-validate/i18n";
import { all as rules } from "@vee-validate/rules";
import { useNavigatorLanguage, useLocalStorage } from "@vueuse/core";
import { configure, defineRule } from "vee-validate";
import { createI18n } from "vue-i18n";
import { z } from "zod";
import { makeZodI18nMap } from "zod-vue-i18n";

import { messages } from "@/.generated/i18n/messages.generated";
import { ELocalStorageKeys } from "@/constants/local-storage-keys.constant";
import { setChartsLocale } from "@/highcharts";

export enum ESupportedLocale {
  FR = "fr",
  EN = "en",
}

export function getLocaleFromString(string?: string): ESupportedLocale {
  const languageValues = Object.values(ESupportedLocale);

  for (const lang in languageValues) {
    if (string?.startsWith(languageValues[lang])) {
      return languageValues[lang];
    }
  }

  return defaultLocale;
}

export function useLocalStorageLanguage() {
  const { language: navigatorLanguage } = useNavigatorLanguage();
  const localStorageLanguage = useLocalStorage(
    ELocalStorageKeys.LANGUAGE_LOCALE_STORAGE_KEY,
    getLocaleFromString(navigatorLanguage.value),
  );

  return {
    localStorageLanguage,
  };
}

function isLocaleSupported(string?: string): boolean {
  if (!string) {
    return false;
  }

  return Object.values(ESupportedLocale).includes(string as ESupportedLocale);
}

function getInitialLocale(): ESupportedLocale {
  const { localStorageLanguage } = useLocalStorageLanguage();

  if (!isLocaleSupported(localStorageLanguage.value)) {
    localStorageLanguage.value = getLocaleFromString(localStorageLanguage.value);
  }

  return localStorageLanguage.value as ESupportedLocale;
}

const defaultLocale = ESupportedLocale.FR;
const initialLocale = getInitialLocale();
const runtimeI18n = createI18n({
  legacy: false,
  fallbackLocale: initialLocale,
  locale: initialLocale,
  messages: messages as never,
});

const i18n = runtimeI18n as unknown as Plugin & {
  global: {
    t: (key: string, params?: Record<string, string | number>) => string;
  };
};

Object.keys(rules).forEach((rule) => {
  defineRule(rule, rules[rule]);
});

setVeeValidateLocale(initialLocale);

configure({
  generateMessage: localize({
    [ESupportedLocale.EN]: (messages.en as Record<string, NonNullable<unknown>>).veeValidate,
    [ESupportedLocale.FR]: (messages.fr as Record<string, NonNullable<unknown>>).veeValidate,
  }),
});

z.setErrorMap(makeZodI18nMap(runtimeI18n as never));

setChartsLocale(initialLocale);

export default i18n;
